import { cn } from "@/lib/cn";
import { useCallback, useState } from "react";

/**
 * Drives a shimmer laid over a photo: it fades out once the photo loads, then
 * unmounts. The photo stays opaque underneath, so it counts as painted as soon
 * as it arrives and a hero photo's paint time isn't delayed by the fade.
 */
export function usePhotoSkeleton() {
  const [state, setState] = useState<"shown" | "fading" | "gone">("shown");
  // Stable, so it only runs on mount: a photo already in memory skips the
  // shimmer instead of flashing it.
  const ref = useCallback((img: HTMLImageElement | null) => {
    if (img?.complete) {
      setState("gone");
    }
  }, []);
  return {
    imgProps: {
      ref,
      onLoad: () =>
        setState((current) => (current === "shown" ? "fading" : current)),
    },
    skeleton:
      state === "gone"
        ? null
        : {
            onTransitionEnd: () => setState("gone"),
            className: cn(
              "animate-skeleton transition-opacity duration-150 ease-out",
              state === "fading" && "opacity-0 motion-reduce:hidden",
            ),
          },
  };
}
